import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { myGroups } from "@/components/recruiting/AddLeadDialog";
import { UserPlus, CheckCircle2, Info, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Referral Partner Add Lead — renders the SAME authoritative "Add Lead" SYSTEM form (role-filtered by the
// server) via the shared FormRenderer, and submits through the protected /referral-partner/leads endpoint
// (authoritative referrer attribution). Editing/publishing Add Lead in the Form Builder propagates here too.
const LEAD_MAP = {
  first_name: "first_name", last_name: "last_name", email: "email", mobile: "mobile",
  city: "city", state: "state", source: "source", notes: "notes", comments: "notes", interested_in: "interest",
};

export default function AddLead() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [consent, setConsent] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const { ctx, activeAccount } = useAuth();
  const groups = myGroups(ctx, activeAccount);
  const [group, setGroup] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/forms/active/add_lead"), api.get("/referral-partner/consent")])
      .then(([f, c]) => { setForm(f.data); setConsent(c.data); })
      .catch((e) => toast.error(apiError(e, "Could not load the Add Lead form.")))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form) return;
    const missing = form.fields.find((f) => f.required && !["section", "display"].includes(f.field_type) && !String(values[f.id] ?? "").trim());
    if (missing) { toast.error(`${missing.label} is required.`); return; }
    if (groups.length > 1 && !group) { toast.error("Choose the Group for this referral."); return; }
    if (!agreed || !consent) { toast.error("Please confirm the consent statement."); return; }
    setBusy(true);
    try {
      const payload = {};
      form.fields.forEach((f) => { if (f.mapping && LEAD_MAP[f.mapping]) payload[LEAD_MAP[f.mapping]] = values[f.id]; });
      Object.assign(payload, { consent: true, consent_version: consent.version, organization_id: group || undefined });
      const { data } = await api.post("/referral-partner/leads", payload);
      setResult(data);
      if (data.status === "created") toast.success("Referral submitted");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const addAnother = () => { setValues({}); setResult(null); setAgreed(false); };

  if (result) {
    const created = result.status === "created";
    const mine = result.status === "already_submitted";
    return (
      <div data-testid="add-lead-result" className="max-w-lg">
        <div className={`rounded-xl border p-6 text-center shadow-sm ${created ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}>
          {created ? <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" /> : <Info className="w-10 h-10 text-slate-400 mx-auto mb-2" />}
          <h2 className="text-lg font-display font-bold text-slate-900" data-testid="add-lead-result-title">
            {created ? "Referral Submitted" : mine ? "Already Submitted" : "Existing Referral"}
          </h2>
          {created ? (
            <>
              <p className="text-slate-700 mt-1">{result.name}</p>
              <p className="text-sm text-slate-500 mt-1">We'll keep you updated as your referral progresses.</p>
              <div className="flex items-center justify-center gap-2 mt-5">
                {result.person_id && <Button variant="outline" onClick={() => navigate(`/referrals/${result.person_id}`)} data-testid="add-lead-view">View Referral</Button>}
                <Button className="bg-brand hover:bg-brand-strong text-white" onClick={addAnother} data-testid="add-lead-another"><UserPlus className="w-4 h-4 mr-1.5" />Add Another Lead</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-2" data-testid="add-lead-result-message">{result.message}</p>
              <div className="flex items-center justify-center gap-2 mt-5">
                {mine && result.person_id && <Button variant="outline" onClick={() => navigate(`/referrals/${result.person_id}`)} data-testid="add-lead-view-existing">View Referral</Button>}
                <Button className="bg-brand hover:bg-brand-strong text-white" onClick={addAnother} data-testid="add-lead-another">Add Another Lead</Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="add-lead-dialog" className="max-w-2xl">
      <button onClick={() => navigate("/referrals")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2" data-testid="add-lead-back"><ArrowLeft className="w-4 h-4" />Back to dashboard</button>
      <PageHeader title={form?.name || "Add Lead"} description={form?.description || "Refer someone you know. We'll match or create their record and keep it attributed to you."} />
      {loading ? (
        <div className="py-16 text-center text-slate-400" data-testid="add-lead-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          {form && <FormRenderer fields={form.fields} values={values} onChange={setValues} />}
          <p className="text-xs text-slate-400">A valid email or mobile helps us reach your referral. Adding at least one is strongly encouraged.</p>
          {groups.length > 1 && (
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger data-testid="referral-group"><SelectValue placeholder="Choose Group *" /></SelectTrigger>
              <SelectContent className="bg-white">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>)}
          {consent && (
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="referral-consent">
              <Checkbox checked={agreed} onCheckedChange={(c) => setAgreed(c === true)} data-testid="referral-consent-checkbox" className="mt-0.5" />
              <span data-testid="referral-consent-text">{consent.text}</span>
            </label>)}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/referrals")} data-testid="add-lead-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !form || !agreed} className="bg-brand hover:bg-brand-strong text-white" data-testid="add-lead-submit"><UserPlus className="w-4 h-4 mr-1.5" />{busy ? "Submitting…" : "Submit Referral"}</Button>
          </div>
        </form>
      )}
    </div>
  );
}
